/**
 * Admin System Alerts
 *
 * Tabbed interface for managing system alerts:
 *  - Dashboard: overview of all alert types with counts and recent activity
 *  - Exercise Gaps: exercises the LLM created that have no Lottie animation
 *
 * Uses the same fetch pattern as LLMUsageAnalytics (admin_token Bearer auth).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { storage } from '../services/storage';
import {
  AlertTriangle,
  Activity,
  Image,
  Search,
  ChevronDown,
  ChevronRight,
  Clock,
  Hash,
  Dumbbell,
  RefreshCw,
  Eye,
  CheckCircle,
  XCircle,
  Filter,
  TrendingUp,
  Upload,
  ClipboardList,
  BarChart3,
  Film,
} from 'lucide-react';

// ── API helper ───────────────────────────────────────────────────────────────

import { API_BASE_URL } from '../utils/apiConfig';

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = await storage.getItem('admin_token');
  if (!token) throw new Error('Admin token not found. Please log in again.');

  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options?.headers,
    },
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

async function apiPost<T>(path: string): Promise<T> {
  return apiFetch<T>(path, { method: 'POST' });
}

// ── Types ────────────────────────────────────────────────────────────────────

type AlertStatus = 'new' | 'reviewed' | 'resolved' | 'ignored';
type TabId = 'dashboard' | 'exercise-gaps' | 'near-misses' | 'file-uploads' | 'plan-quality' | 'corpus-health';

// ── Corpus Health types (Phase 1.3) ──────────────────────────────────────────
// Mirrors the JSON returned by /api/admin/lottie-diagnostics (Phase 1.2).

interface CorpusFilesVsRegistry {
  files_on_disk_count: number;
  registry_entries_count: number;
  valid_mappings: number;
  orphan_files: string[];
  dangling_refs: string[];
  orphan_count: number;
  dangling_count: number;
}

interface CorpusSlugViolation {
  slug: string;
  display_name: string;
  internal_name: string;
  suggested_aliases: string[];
  has_aliases: boolean;
  reason: string;
}

interface CorpusSlugQuality {
  total_entries_scanned: number;
  violation_count: number;
  violations: CorpusSlugViolation[];
}

interface CorpusTopGap {
  normalized_slug: string;
  exercise_name: string;
  occurrences: number;
  last_seen: string | null;
  triage: 'apostrophe_normalization' | 'slug_alias_needed' | 'genuine_corpus_gap' | string;
}

interface CorpusNearMiss {
  normalized_slug: string;
  exercise_name: string;
  matched_slug: string;
  match_confidence: number;
  match_tier: number;
  occurrences: number;
  likely_false_positive: boolean;
}

interface CorpusProductionGaps {
  days_window: number;
  top_gaps: CorpusTopGap[];
  top_near_misses: CorpusNearMiss[];
  triage_summary: Record<string, number>;
}

interface CorpusCoverageRow {
  content_type: string;
  total: number;
  with_field: number;
  with_animation: number;
  placeholders: number;
  coverage_pct: number;
}

interface CorpusCoverage {
  days_window: number;
  by_content_type: CorpusCoverageRow[];
  overall_total: number;
  overall_with_animation: number;
  overall_coverage_pct: number;
}

interface CorpusDiagnostics {
  generated_at_utc: string;
  files_vs_registry: CorpusFilesVsRegistry;
  slug_quality: CorpusSlugQuality;
  production_gaps: CorpusProductionGaps;
  coverage: CorpusCoverage;
  cached: boolean;
}

interface ExerciseGapAlert {
  id: string;
  status: AlertStatus;
  exercise_name: string;
  normalized_slug?: string;
  likely_cause: string;
  suggested_category?: string;
  equipment: string;
  target_muscles: string[];
  occurrence_count: number;
  last_seen: string;
  created_at: string;
  user_id?: string;
  workout_type?: string;
  llm_output?: {
    sets?: number;
    reps?: string;
    form_cues?: string[];
    notes?: string;
    equipment?: string[];
    target_muscles?: string[];
  };
}

interface ExerciseGapResponse {
  alerts: ExerciseGapAlert[];
  total: number;
  status_counts: Record<AlertStatus, number>;
}

interface NearMissExerciseInfo {
  display_name?: string;
  modality?: string;
  movement_type?: string;
  target_muscles?: string[];
  secondary_muscles?: string[];
  equipment?: string[];
  difficulty?: string;
  body_position?: string;
  movement_pattern?: string;
  tags?: string[];
}

interface NearMissAlert {
  id: string;
  status: AlertStatus;
  exercise_name: string;
  normalized_slug?: string;
  matched_slug: string;
  matched_display_name: string;
  match_confidence: number;
  match_tier: number;
  animation_path: string;
  occurrence_count: number;
  last_seen: string;
  created_at: string;
  user_id?: string;
  workout_type?: string;
  exercise_info?: NearMissExerciseInfo;
}

interface NearMissResponse {
  alerts: NearMissAlert[];
  total: number;
  status_counts: Record<AlertStatus, number>;
}

interface FileUploadAlert {
  id: string;
  status: AlertStatus;
  user_id: string | null;
  filename: string;
  file_extension: string;
  detected_file_type: string;
  content_type: string;
  processing_status: string;
  alert_reason: string;
  error_details: string | null;
  occurrence_count: number;
  created_at: string;
  last_seen: string;
}

interface FileUploadResponse {
  alerts: FileUploadAlert[];
  total: number;
  status_counts: Record<AlertStatus, number>;
}

interface AlertSummary {
  total_new: number;
  total_unique_exercises: number;
  most_requested: string | null;
  last_alert_timestamp: string | null;
  status_counts: Record<AlertStatus, number>;
  recent_alerts: ExerciseGapAlert[];
  file_upload_new: number;
}

// ── Status helpers ───────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<AlertStatus, { label: string; bg: string; text: string; border: string }> = {
  new: { label: 'New', bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
  reviewed: { label: 'Reviewed', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  resolved: { label: 'Resolved', bg: 'bg-[var(--cedar-subtle)]', text: 'text-[var(--cedar-primary)]', border: 'border-[var(--cedar-mist)]' },
  ignored: { label: 'Ignored', bg: 'bg-gray-50', text: 'text-gray-500', border: 'border-gray-200' },
};

function StatusBadge({ status }: { status: AlertStatus }) {
  const cfg = STATUS_CONFIG[status];
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
}

function formatTimestamp(iso: string): string {
  if (!iso) return '--';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Main component ───────────────────────────────────────────────────────────

export const AdminSystemAlerts: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>('dashboard');

  // Dashboard state
  const [summary, setSummary] = useState<AlertSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  // Exercise Gaps state
  const [alerts, setAlerts] = useState<ExerciseGapAlert[]>([]);
  const [alertsTotal, setAlertsTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<AlertStatus, number>>({
    new: 0, reviewed: 0, resolved: 0, ignored: 0,
  });
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [alertsError, setAlertsError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<AlertStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
  const [fileUpdatingIds, setFileUpdatingIds] = useState<Set<string>>(new Set());

  // File Uploads state
  const [fileAlerts, setFileAlerts] = useState<FileUploadAlert[]>([]);
  const [fileAlertsTotal, setFileAlertsTotal] = useState(0);
  const [fileStatusCounts, setFileStatusCounts] = useState<Record<AlertStatus, number>>({
    new: 0, reviewed: 0, resolved: 0, ignored: 0,
  });
  const [fileAlertsLoading, setFileAlertsLoading] = useState(true);
  const [fileAlertsError, setFileAlertsError] = useState<string | null>(null);
  const [fileStatusFilter, setFileStatusFilter] = useState<AlertStatus | 'all'>('all');
  const [fileSearchQuery, setFileSearchQuery] = useState('');
  const [fileExpandedRows, setFileExpandedRows] = useState<Set<string>>(new Set());

  // Near Misses state
  const [nearMissAlerts, setNearMissAlerts] = useState<NearMissAlert[]>([]);
  const [nearMissTotal, setNearMissTotal] = useState(0);
  const [nearMissStatusCounts, setNearMissStatusCounts] = useState<Record<AlertStatus, number>>({
    new: 0, reviewed: 0, resolved: 0, ignored: 0,
  });
  const [nearMissLoading, setNearMissLoading] = useState(true);
  const [nearMissError, setNearMissError] = useState<string | null>(null);
  const [nearMissStatusFilter, setNearMissStatusFilter] = useState<AlertStatus | 'all'>('all');
  const [nearMissSearchQuery, setNearMissSearchQuery] = useState('');
  const [nearMissExpandedRows, setNearMissExpandedRows] = useState<Set<string>>(new Set());
  const [nearMissUpdatingIds, setNearMissUpdatingIds] = useState<Set<string>>(new Set());

  // Plan Quality state
  const [planQualityData, setPlanQualityData] = useState<PlanQualityResponse | null>(null);
  const [planQualityLoading, setPlanQualityLoading] = useState(true);
  const [planQualityError, setPlanQualityError] = useState<string | null>(null);
  const [planQualityExpanded, setPlanQualityExpanded] = useState<Set<string>>(new Set());

  // Corpus Health state (Phase 1.3)
  const [corpusData, setCorpusData] = useState<CorpusDiagnostics | null>(null);
  const [corpusLoading, setCorpusLoading] = useState(true);
  const [corpusError, setCorpusError] = useState<string | null>(null);

  // ── Data fetching ────────────────────────────────────────────────────────

  const fetchExerciseGaps = useCallback(async () => {
    setAlertsLoading(true);
    setAlertsError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('limit', '200');
      const qs = params.toString();
      const data = await apiFetch<ExerciseGapResponse>(
        `/api/admin/alerts/exercise-gaps${qs ? `?${qs}` : ''}`
      );
      setAlerts(data.alerts || []);
      setAlertsTotal(data.total ?? (data.alerts || []).length);
      setStatusCounts(data.status_counts || { new: 0, reviewed: 0, resolved: 0, ignored: 0 });
    } catch (err: any) {
      console.error('Failed to load exercise gap alerts:', err);
      setAlertsError(err.message || 'Failed to load alerts');
      setAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  }, [statusFilter]);

  const fetchFileAlerts = useCallback(async () => {
    setFileAlertsLoading(true);
    setFileAlertsError(null);
    try {
      const params = new URLSearchParams();
      if (fileStatusFilter !== 'all') params.set('status', fileStatusFilter);
      params.set('limit', '200');
      const qs = params.toString();
      const data = await apiFetch<FileUploadResponse>(
        `/api/admin/alerts/unsupported-files${qs ? `?${qs}` : ''}`
      );
      setFileAlerts(data.alerts || []);
      setFileAlertsTotal(data.total ?? (data.alerts || []).length);
      setFileStatusCounts(data.status_counts || { new: 0, reviewed: 0, resolved: 0, ignored: 0 });
    } catch (err: any) {
      console.error('Failed to load file upload alerts:', err);
      setFileAlertsError(err.message || 'Failed to load file upload alerts');
      setFileAlerts([]);
    } finally {
      setFileAlertsLoading(false);
    }
  }, [fileStatusFilter]);

  const fetchNearMisses = useCallback(async () => {
    setNearMissLoading(true);
    setNearMissError(null);
    try {
      const params = new URLSearchParams();
      if (nearMissStatusFilter !== 'all') params.set('status', nearMissStatusFilter);
      params.set('limit', '200');
      const qs = params.toString();
      const data = await apiFetch<NearMissResponse>(
        `/api/admin/alerts/near-miss-matches${qs ? `?${qs}` : ''}`
      );
      setNearMissAlerts(data.alerts || []);
      setNearMissTotal(data.total ?? (data.alerts || []).length);
      setNearMissStatusCounts(data.status_counts || { new: 0, reviewed: 0, resolved: 0, ignored: 0 });
    } catch (err: any) {
      console.error('Failed to load near-miss alerts:', err);
      setNearMissError(err.message || 'Failed to load near-miss alerts');
      setNearMissAlerts([]);
    } finally {
      setNearMissLoading(false);
    }
  }, [nearMissStatusFilter]);

  const fetchSummary = useCallback(async () => {
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      // Fetch all alerts to build summary (the dashboard needs aggregate data)
      const emptyExerciseResponse: ExerciseGapResponse = { alerts: [], total: 0, status_counts: { new: 0, reviewed: 0, resolved: 0, ignored: 0 } };
      const emptyFileResponse: FileUploadResponse = { alerts: [], total: 0, status_counts: { new: 0, reviewed: 0, resolved: 0, ignored: 0 } };
      const [data, fileData] = await Promise.all([
        apiFetch<ExerciseGapResponse>('/api/admin/alerts/exercise-gaps?limit=200').catch(() => emptyExerciseResponse),
        apiFetch<FileUploadResponse>('/api/admin/alerts/unsupported-files?limit=200').catch(() => emptyFileResponse),
      ]);
      const allAlerts = data.alerts || [];
      const counts = data.status_counts || { new: 0, reviewed: 0, resolved: 0, ignored: 0 };
      const fileUploadNew = fileData.status_counts?.new || 0;

      // Compute derived summary
      const newAlerts = allAlerts.filter(a => a.status === 'new');
      const uniqueExercises = new Set(allAlerts.map(a => a.exercise_name.toLowerCase())).size;

      // Most requested = highest occurrence_count
      let mostRequested: string | null = null;
      let maxCount = 0;
      for (const a of allAlerts) {
        if (a.occurrence_count > maxCount) {
          maxCount = a.occurrence_count;
          mostRequested = a.exercise_name;
        }
      }

      // Last alert timestamp
      const sorted = [...allAlerts].sort(
        (a, b) => new Date(b.created_at || b.last_seen).getTime() - new Date(a.created_at || a.last_seen).getTime()
      );
      const lastTimestamp = sorted.length > 0 ? (sorted[0].created_at || sorted[0].last_seen) : null;

      setSummary({
        total_new: newAlerts.length,
        total_unique_exercises: uniqueExercises,
        most_requested: mostRequested,
        last_alert_timestamp: lastTimestamp,
        status_counts: counts,
        recent_alerts: sorted.slice(0, 5),
        file_upload_new: fileUploadNew,
      });
    } catch (err: any) {
      console.error('Failed to load alert summary:', err);
      setSummaryError(err.message || 'Failed to load summary');
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const fetchPlanQuality = useCallback(async () => {
    setPlanQualityLoading(true);
    setPlanQualityError(null);
    try {
      const data = await apiFetch<PlanQualityResponse>('/api/admin/system/alerts/plan-quality?days=7');
      setPlanQualityData(data);
    } catch (err: any) {
      console.error('Failed to load plan quality alerts:', err);
      setPlanQualityError(err.message || 'Failed to load plan quality alerts');
    } finally {
      setPlanQualityLoading(false);
    }
  }, []);

  const fetchCorpusHealth = useCallback(async (fresh: boolean = false) => {
    setCorpusLoading(true);
    setCorpusError(null);
    try {
      const params = fresh ? '?fresh=true' : '';
      const data = await apiFetch<CorpusDiagnostics>(
        `/api/admin/lottie-diagnostics${params}`
      );
      setCorpusData(data);
    } catch (err: any) {
      console.error('Failed to load Lottie corpus diagnostics:', err);
      setCorpusError(err.message || 'Failed to load corpus diagnostics');
    } finally {
      setCorpusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchExerciseGaps();
    fetchNearMisses();
    fetchFileAlerts();
    fetchPlanQuality();
    fetchCorpusHealth();
  }, [fetchSummary, fetchExerciseGaps, fetchNearMisses, fetchFileAlerts, fetchPlanQuality, fetchCorpusHealth]);

  // Re-fetch gaps when filter changes
  useEffect(() => {
    fetchExerciseGaps();
  }, [statusFilter, fetchExerciseGaps]);

  // Re-fetch file alerts when filter changes
  useEffect(() => {
    fetchFileAlerts();
  }, [fileStatusFilter, fetchFileAlerts]);

  // Re-fetch near-miss alerts when filter changes
  useEffect(() => {
    fetchNearMisses();
  }, [nearMissStatusFilter, fetchNearMisses]);

  // ── Status update ────────────────────────────────────────────────────────

  const [updateError, setUpdateError] = useState<string | null>(null);

  const updateAlertStatus = async (alertId: string, newStatus: AlertStatus) => {
    setUpdatingIds(prev => new Set(prev).add(alertId));
    setUpdateError(null);
    try {
      await apiPost(`/api/admin/alerts/exercise-gaps/${alertId}/status?status=${newStatus}`);
      // Post-confirmation update — UI updates only after server confirms
      setAlerts(prev =>
        prev.map(a => (a.id === alertId ? { ...a, status: newStatus } : a))
      );
      // Refresh summary counts
      fetchSummary();
    } catch (err: any) {
      console.error('Failed to update alert status:', err);
      setUpdateError(`Failed to update alert: ${err.message || 'Unknown error'}`);
      // Auto-clear error after 5 seconds
      setTimeout(() => setUpdateError(null), 5000);
    } finally {
      setUpdatingIds(prev => {
        const next = new Set(prev);
        next.delete(alertId);
        return next;
      });
    }
  };

  const updateFileAlertStatus = async (alertId: string, newStatus: AlertStatus) => {
    setFileUpdatingIds(prev => new Set(prev).add(alertId));
    setUpdateError(null);
    try {
      await apiPost(`/api/admin/alerts/unsupported-files/${alertId}/status?status=${newStatus}`);
      setFileAlerts(prev =>
        prev.map(a => (a.id === alertId ? { ...a, status: newStatus } : a))
      );
      fetchSummary();
    } catch (err: any) {
      console.error('Failed to update file alert status:', err);
      setUpdateError(`Failed to update alert: ${err.message || 'Unknown error'}`);
      setTimeout(() => setUpdateError(null), 5000);
    } finally {
      setFileUpdatingIds(prev => {
        const next = new Set(prev);
        next.delete(alertId);
        return next;
      });
    }
  };

  // ── Row expand ───────────────────────────────────────────────────────────

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleFileRow = (id: string) => {
    setFileExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateNearMissStatus = async (alertId: string, newStatus: AlertStatus) => {
    setNearMissUpdatingIds(prev => new Set(prev).add(alertId));
    setUpdateError(null);
    try {
      await apiPost(`/api/admin/alerts/near-miss-matches/${alertId}/status?status=${newStatus}`);
      setNearMissAlerts(prev =>
        prev.map(a => (a.id === alertId ? { ...a, status: newStatus } : a))
      );
      fetchSummary();
    } catch (err: any) {
      console.error('Failed to update near-miss alert status:', err);
      setUpdateError(`Failed to update alert: ${err.message || 'Unknown error'}`);
      setTimeout(() => setUpdateError(null), 5000);
    } finally {
      setNearMissUpdatingIds(prev => {
        const next = new Set(prev);
        next.delete(alertId);
        return next;
      });
    }
  };

  const toggleNearMissRow = (id: string) => {
    setNearMissExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Search filtering (client-side on already-fetched data) ───────────────

  const filteredAlerts = alerts.filter(a => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      a.exercise_name.toLowerCase().includes(q) ||
      a.likely_cause?.toLowerCase().includes(q) ||
      a.equipment?.toLowerCase().includes(q) ||
      (a.target_muscles || []).some(m => m.toLowerCase().includes(q))
    );
  });

  const filteredFileAlerts = fileAlerts.filter(a => {
    if (!fileSearchQuery.trim()) return true;
    const q = fileSearchQuery.toLowerCase();
    return (
      a.filename.toLowerCase().includes(q) ||
      a.file_extension.toLowerCase().includes(q) ||
      a.alert_reason.toLowerCase().includes(q) ||
      a.content_type.toLowerCase().includes(q)
    );
  });

  // Top 10 most requested
  const topMissing = [...alerts]
    .sort((a, b) => b.occurrence_count - a.occurrence_count)
    .slice(0, 10);

  const filteredNearMissAlerts = nearMissAlerts.filter(a => {
    if (!nearMissSearchQuery.trim()) return true;
    const q = nearMissSearchQuery.toLowerCase();
    return (
      a.exercise_name.toLowerCase().includes(q) ||
      a.matched_slug.toLowerCase().includes(q) ||
      a.matched_display_name.toLowerCase().includes(q)
    );
  });

  // ── Tab rendering ────────────────────────────────────────────────────────

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: Activity },
    { id: 'exercise-gaps', label: 'Exercise Gaps', icon: Image },
    { id: 'near-misses', label: 'Near Misses', icon: Search },
    { id: 'file-uploads', label: 'File Uploads', icon: Upload },
    { id: 'plan-quality', label: 'Plan Quality', icon: ClipboardList },
    { id: 'corpus-health', label: 'Corpus Health', icon: BarChart3 },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Error toast for status update failures */}
      {updateError && (
        <div className="fixed top-4 right-4 z-50 bg-red-50 border border-red-200 rounded-lg px-4 py-3 shadow-lg flex items-center gap-2 max-w-md animate-in fade-in">
          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-sm text-red-700">{updateError}</span>
          <button onClick={() => setUpdateError(null)} className="ml-2 text-red-400 hover:text-red-600">
            &times;
          </button>
        </div>
      )}
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "'League Spartan', sans-serif", color: 'var(--text-primary)' }}>
            <AlertTriangle className="h-6 w-6 text-[var(--cedar-primary)]" />
            System Alerts
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Exercise gaps, file uploads &amp; system notifications
          </p>
        </div>
        <button
          onClick={() => { fetchSummary(); fetchExerciseGaps(); fetchNearMisses(); fetchFileAlerts(); fetchPlanQuality(); fetchCorpusHealth(true); }}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Tab bar */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-[var(--cedar-primary)] text-[var(--cedar-primary)]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                {tab.id === 'exercise-gaps' && statusCounts.new > 0 && (
                  <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    {statusCounts.new}
                  </span>
                )}
                {tab.id === 'near-misses' && nearMissStatusCounts.new > 0 && (
                  <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    {nearMissStatusCounts.new}
                  </span>
                )}
                {tab.id === 'file-uploads' && fileStatusCounts.new > 0 && (
                  <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    {fileStatusCounts.new}
                  </span>
                )}
                {tab.id === 'plan-quality' && planQualityData?.summary?.total_issues > 0 && (
                  <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    {planQualityData.summary.total_issues}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'dashboard' && (
        <DashboardTab
          summary={summary}
          loading={summaryLoading}
          error={summaryError}
          onRetry={fetchSummary}
        />
      )}
      {activeTab === 'exercise-gaps' && (
        <ExerciseGapsTab
          alerts={filteredAlerts}
          total={alertsTotal}
          statusCounts={statusCounts}
          loading={alertsLoading}
          error={alertsError}
          statusFilter={statusFilter}
          searchQuery={searchQuery}
          expandedRows={expandedRows}
          updatingIds={updatingIds}
          topMissing={topMissing}
          onStatusFilterChange={setStatusFilter}
          onSearchChange={setSearchQuery}
          onToggleRow={toggleRow}
          onUpdateStatus={updateAlertStatus}
          onRetry={fetchExerciseGaps}
        />
      )}
      {activeTab === 'near-misses' && (
        <NearMissesTab
          alerts={filteredNearMissAlerts}
          total={nearMissTotal}
          statusCounts={nearMissStatusCounts}
          loading={nearMissLoading}
          error={nearMissError}
          statusFilter={nearMissStatusFilter}
          searchQuery={nearMissSearchQuery}
          expandedRows={nearMissExpandedRows}
          updatingIds={nearMissUpdatingIds}
          onStatusFilterChange={setNearMissStatusFilter}
          onSearchChange={setNearMissSearchQuery}
          onToggleRow={toggleNearMissRow}
          onUpdateStatus={updateNearMissStatus}
          onRetry={fetchNearMisses}
        />
      )}
      {activeTab === 'file-uploads' && (
        <FileUploadsTab
          alerts={filteredFileAlerts}
          total={fileAlertsTotal}
          statusCounts={fileStatusCounts}
          loading={fileAlertsLoading}
          error={fileAlertsError}
          statusFilter={fileStatusFilter}
          searchQuery={fileSearchQuery}
          expandedRows={fileExpandedRows}
          updatingIds={fileUpdatingIds}
          onStatusFilterChange={setFileStatusFilter}
          onSearchChange={setFileSearchQuery}
          onToggleRow={toggleFileRow}
          onUpdateStatus={updateFileAlertStatus}
          onRetry={fetchFileAlerts}
        />
      )}
      {activeTab === 'plan-quality' && (
        <PlanQualityTab
          data={planQualityData}
          loading={planQualityLoading}
          error={planQualityError}
          expandedCategories={planQualityExpanded}
          onToggleCategory={(cat: string) => {
            setPlanQualityExpanded(prev => {
              const next = new Set(prev);
              next.has(cat) ? next.delete(cat) : next.add(cat);
              return next;
            });
          }}
          onRetry={fetchPlanQuality}
        />
      )}
      {activeTab === 'corpus-health' && (
        <CorpusHealthTab
          data={corpusData}
          loading={corpusLoading}
          error={corpusError}
          onRefresh={() => fetchCorpusHealth(true)}
        />
      )}
    </div>
  );
};

// ── Dashboard Tab ────────────────────────────────────────────────────────────

interface DashboardTabProps {
  summary: AlertSummary | null;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
}

function DashboardTab({ summary, loading, error, onRetry }: DashboardTabProps) {
  if (loading) return <LoadingState message="Loading alert summary..." />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;
  if (!summary) return <EmptyState />;

  const { total_new, total_unique_exercises, most_requested, last_alert_timestamp, status_counts, recent_alerts, file_upload_new } = summary;
  const totalAll = (status_counts.new || 0) + (status_counts.reviewed || 0) + (status_counts.resolved || 0) + (status_counts.ignored || 0);

  if (totalAll === 0 && !file_upload_new) return <EmptyState />;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <SummaryCard
          icon={AlertTriangle}
          iconColor="text-yellow-600"
          iconBg="bg-yellow-50"
          label="New Exercise Alerts"
          value={total_new}
        />
        <SummaryCard
          icon={Upload}
          iconColor="text-orange-600"
          iconBg="bg-orange-50"
          label="New File Upload Alerts"
          value={file_upload_new}
        />
        <SummaryCard
          icon={Dumbbell}
          iconColor="text-[var(--cedar-primary)]"
          iconBg="bg-[var(--cedar-subtle)]"
          label="Unique Exercises Missing"
          value={total_unique_exercises}
        />
        <SummaryCard
          icon={TrendingUp}
          iconColor="text-blue-600"
          iconBg="bg-blue-50"
          label="Most Requested Missing"
          value={most_requested || '--'}
          isText
        />
        <SummaryCard
          icon={Clock}
          iconColor="text-gray-600"
          iconBg="bg-gray-100"
          label="Last Alert"
          value={last_alert_timestamp ? formatTimestamp(last_alert_timestamp) : '--'}
          isText
        />
      </div>

      {/* Status distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Status Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {(Object.keys(STATUS_CONFIG) as AlertStatus[]).map(status => {
              const count = status_counts[status] || 0;
              const cfg = STATUS_CONFIG[status];
              const pct = totalAll > 0 ? Math.round((count / totalAll) * 100) : 0;
              return (
                <div
                  key={status}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${cfg.bg} ${cfg.border}`}
                >
                  <span className={`text-lg font-bold ${cfg.text}`}>{count}</span>
                  <div className="text-xs">
                    <div className={`font-medium ${cfg.text}`}>{cfg.label}</div>
                    <div className="text-gray-400">{pct}%</div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Progress bar */}
          <div className="mt-4 h-2 rounded-full bg-gray-100 overflow-hidden flex">
            {totalAll > 0 && (
              <>
                <div className="bg-yellow-400" style={{ width: `${((status_counts.new || 0) / totalAll) * 100}%` }} />
                <div className="bg-blue-400" style={{ width: `${((status_counts.reviewed || 0) / totalAll) * 100}%` }} />
                <div className="bg-green-400" style={{ width: `${((status_counts.resolved || 0) / totalAll) * 100}%` }} />
                <div className="bg-gray-300" style={{ width: `${((status_counts.ignored || 0) / totalAll) * 100}%` }} />
              </>
            )}
          </div>
          <div className="mt-2 flex gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block" /> New</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-400 inline-block" /> Reviewed</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-400 inline-block" /> Resolved</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-300 inline-block" /> Ignored</span>
          </div>
        </CardContent>
      </Card>

      {/* Recent alerts */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Alerts</CardTitle>
        </CardHeader>
        <CardContent>
          {recent_alerts.length === 0 ? (
            <p className="text-sm text-gray-500">No recent alerts.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {recent_alerts.map(alert => (
                <div key={alert.id} className="py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <StatusBadge status={alert.status} />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {alert.exercise_name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {alert.likely_cause || 'Unknown cause'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <span className="text-xs text-gray-400 flex items-center gap-1">
                      <Hash className="h-3 w-3" />
                      {alert.occurrence_count}x
                    </span>
                    <span className="text-xs text-gray-400">
                      {formatTimestamp(alert.created_at || alert.last_seen)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Exercise Gaps Tab ────────────────────────────────────────────────────────

interface ExerciseGapsTabProps {
  alerts: ExerciseGapAlert[];
  total: number;
  statusCounts: Record<AlertStatus, number>;
  loading: boolean;
  error: string | null;
  statusFilter: AlertStatus | 'all';
  searchQuery: string;
  expandedRows: Set<string>;
  updatingIds: Set<string>;
  topMissing: ExerciseGapAlert[];
  onStatusFilterChange: (filter: AlertStatus | 'all') => void;
  onSearchChange: (query: string) => void;
  onToggleRow: (id: string) => void;
  onUpdateStatus: (id: string, status: AlertStatus) => void;
  onRetry: () => void;
}

function ExerciseGapsTab({
  alerts,
  total,
  statusCounts,
  loading,
  error,
  statusFilter,
  searchQuery,
  expandedRows,
  updatingIds,
  topMissing,
  onStatusFilterChange,
  onSearchChange,
  onToggleRow,
  onUpdateStatus,
  onRetry,
}: ExerciseGapsTabProps) {
  if (loading) return <LoadingState message="Loading exercise gap alerts..." />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;

  const totalAll = (statusCounts.new || 0) + (statusCounts.reviewed || 0) + (statusCounts.resolved || 0) + (statusCounts.ignored || 0);

  if (totalAll === 0 && statusFilter === 'all' && !searchQuery) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-6">
      {/* Top Missing Exercises */}
      {topMissing.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[var(--cedar-primary)]" />
              Top Missing Exercises
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
              {topMissing.map((alert, idx) => (
                <div
                  key={alert.id}
                  className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50"
                >
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[var(--cedar-primary)] text-white text-xs font-bold flex items-center justify-center">
                    {idx + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {alert.exercise_name}
                    </p>
                    <p className="text-xs text-gray-500">{alert.occurrence_count} requests</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        {/* Status filter */}
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {(['all', 'new', 'reviewed', 'resolved', 'ignored'] as const).map(s => (
              <button
                key={s}
                onClick={() => onStatusFilterChange(s)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-[var(--cedar-primary)] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                } ${s !== 'all' ? 'border-l border-gray-200' : ''}`}
              >
                {s === 'all' ? 'All' : STATUS_CONFIG[s].label}
                {s !== 'all' && (
                  <span className="ml-1 text-[10px] opacity-70">
                    ({statusCounts[s] || 0})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Search */}
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search exercises..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--cedar-primary)]/20 focus:border-[var(--cedar-primary)]"
          />
        </div>

        {/* Result count */}
        <span className="text-xs text-gray-400 ml-auto">
          Showing {alerts.length} of {total} alerts
        </span>
      </div>

      {/* Alerts table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="text-sm" style={{ minWidth: 1100 }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--cedar-limestone)', borderBottom: '1px solid var(--cedar-limestone)' }}>
                <th className="text-left py-3 px-4 font-medium text-gray-500 w-8" />
                <th className="text-left py-3 px-4 font-medium text-gray-500" style={{ minWidth: 90 }}>Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500" style={{ minWidth: 260 }}>Exercise Name</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500" style={{ minWidth: 180 }}>Likely Cause</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500" style={{ minWidth: 140 }}>Equipment</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500" style={{ minWidth: 180 }}>Target Muscles</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500" style={{ minWidth: 60 }}>Count</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500" style={{ minWidth: 100 }}>Last Seen</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500" style={{ minWidth: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {alerts.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-gray-400">
                    {searchQuery
                      ? `No alerts matching "${searchQuery}"`
                      : 'No alerts for the selected filter.'}
                  </td>
                </tr>
              ) : (
                alerts.map(alert => (
                  <AlertRow
                    key={alert.id}
                    alert={alert}
                    isExpanded={expandedRows.has(alert.id)}
                    isUpdating={updatingIds.has(alert.id)}
                    onToggle={() => onToggleRow(alert.id)}
                    onUpdateStatus={onUpdateStatus}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── Near Misses Tab ─────────────────────────────────────────────────────────

interface NearMissesTabProps {
  alerts: NearMissAlert[];
  total: number;
  statusCounts: Record<AlertStatus, number>;
  loading: boolean;
  error: string | null;
  statusFilter: AlertStatus | 'all';
  searchQuery: string;
  expandedRows: Set<string>;
  updatingIds: Set<string>;
  onStatusFilterChange: (filter: AlertStatus | 'all') => void;
  onSearchChange: (query: string) => void;
  onToggleRow: (id: string) => void;
  onUpdateStatus: (id: string, status: AlertStatus) => void;
  onRetry: () => void;
}

// ── Lottie Admin Preview ─────────────────────────────────────────────────────
// Renders a Lottie animation in the browser using lottie-web (dynamic import).
// Fetches from the backend's /api/assets/lottie/{path} endpoint.

function LottieAdminPreview({ animationPath, displayName }: { animationPath: string; displayName: string }) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [status, setStatus] = React.useState<'loading' | 'loaded' | 'error'>('loading');

  React.useEffect(() => {
    if (!animationPath || !containerRef.current) return;
    let anim: any = null;

    const encodedPath = animationPath
      .split('/')
      .map(encodeURIComponent)
      .join('/');
    const url = `${API_BASE_URL}/api/assets/lottie/${encodedPath}`;

    import('lottie-web').then((lottieModule) => {
      const lottie = lottieModule.default;
      if (!containerRef.current) return;
      try {
        anim = lottie.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          path: url,
        });
        anim.addEventListener('DOMLoaded', () => setStatus('loaded'));
        anim.addEventListener('error', () => setStatus('error'));
        // Fallback: mark loaded after 5s if event never fires
        const timer = setTimeout(() => setStatus(s => s === 'loading' ? 'error' : s), 5000);
        anim._adminTimer = timer;
      } catch {
        setStatus('error');
      }
    }).catch(() => setStatus('error'));

    return () => {
      if (anim) {
        clearTimeout(anim._adminTimer);
        anim.destroy();
      }
    };
  }, [animationPath]);

  return (
    <div style={{ width: 140, height: 140, flexShrink: 0, position: 'relative', background: 'var(--surface-green-soft)', borderRadius: 10, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {status === 'loading' && (
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 24, height: 24, border: '2px solid var(--cedar-mist)', borderTopColor: 'var(--cedar-primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      )}
      {status === 'error' && (
        <div style={{ textAlign: 'center', padding: 12 }}>
          <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'center' }}><Film size={28} color="var(--text-tertiary)" /></div>
          <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Preview unavailable</div>
        </div>
      )}
      <div ref={containerRef} style={{ width: 140, height: 140, opacity: status === 'loaded' ? 1 : 0 }} />
    </div>
  );
}

// ── Exercise Info Description ─────────────────────────────────────────────────

function ExerciseInfoPanel({ info, animationPath, displayName }: {
  info: NearMissExerciseInfo;
  animationPath: string;
  displayName: string;
}) {
  const muscles = [
    ...(info.target_muscles ?? []),
  ].map(m => m.replace(/_/g, ' '));
  const equipment = (info.equipment ?? []).map(e => e.replace(/_/g, ' '));

  return (
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      {/* Lottie visual */}
      <div>
        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Visual (what user sees)</div>
        <LottieAdminPreview animationPath={animationPath} displayName={displayName} />
      </div>

      {/* Metadata */}
      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', fontSize: 13 }}>
        {info.modality && (
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 2 }}>Modality</div>
            <div style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{info.modality} · {info.movement_type ?? ''}</div>
          </div>
        )}
        {info.difficulty && (
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 2 }}>Difficulty</div>
            <div style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{info.difficulty}</div>
          </div>
        )}
        {muscles.length > 0 && (
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 2 }}>Target Muscles</div>
            <div style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{muscles.join(', ')}</div>
          </div>
        )}
        {equipment.length > 0 && (
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 2 }}>Equipment</div>
            <div style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{equipment.join(', ')}</div>
          </div>
        )}
        {info.movement_pattern && (
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 2 }}>Movement Pattern</div>
            <div style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{info.movement_pattern}</div>
          </div>
        )}
        {info.body_position && (
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 2 }}>Body Position</div>
            <div style={{ color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{info.body_position}</div>
          </div>
        )}
        {(info.tags ?? []).length > 0 && (
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ fontWeight: 600, color: 'var(--text-strong)', marginBottom: 4 }}>Tags</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {(info.tags ?? []).map(tag => (
                <span key={tag} style={{ padding: '2px 8px', background: 'var(--cedar-subtle)', color: 'var(--cedar-primary)', borderRadius: 12, fontSize: 11, fontWeight: 500 }}>
                  {tag.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function confidenceColor(confidence: number): string {
  if (confidence >= 0.65) return 'text-[var(--cedar-primary)] bg-[var(--cedar-subtle)] border-[var(--cedar-mist)]';
  if (confidence >= 0.5) return 'text-yellow-700 bg-yellow-50 border-yellow-200';
  return 'text-orange-700 bg-orange-50 border-orange-200';
}

function tierLabel(tier: number): string {
  switch (tier) {
    case 3: return 'Word Overlap';
    case 4: return 'Partial Match';
    default: return `Tier ${tier}`;
  }
}

function NearMissesTab({
  alerts,
  total,
  statusCounts,
  loading,
  error,
  statusFilter,
  searchQuery,
  expandedRows,
  updatingIds,
  onStatusFilterChange,
  onSearchChange,
  onToggleRow,
  onUpdateStatus,
  onRetry,
}: NearMissesTabProps) {
  if (loading) return <LoadingState message="Loading near-miss alerts..." />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;

  const totalAll = (statusCounts.new || 0) + (statusCounts.reviewed || 0) + (statusCounts.resolved || 0) + (statusCounts.ignored || 0);

  if (totalAll === 0 && statusFilter === 'all' && !searchQuery) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Search className="mx-auto h-12 w-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-600">No Near-Miss Alerts</h3>
          <p className="text-sm text-gray-400 mt-2 max-w-md mx-auto">
            Near-miss alerts appear when a fuzzy Lottie match is used with low confidence.
            These help you tune matching quality over time.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {(['all', 'new', 'reviewed', 'resolved', 'ignored'] as const).map(s => (
              <button
                key={s}
                onClick={() => onStatusFilterChange(s)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-[var(--cedar-primary)] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                } ${s !== 'all' ? 'border-l border-gray-200' : ''}`}
              >
                {s === 'all' ? 'All' : STATUS_CONFIG[s].label}
                {s !== 'all' && (
                  <span className="ml-1 text-[10px] opacity-70">
                    ({statusCounts[s] || 0})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search exercises or matched slugs..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--cedar-primary)]/20 focus:border-[var(--cedar-primary)]"
          />
        </div>

        <span className="text-xs text-gray-400 ml-auto">
          Showing {alerts.length} of {total} alerts
        </span>
      </div>

      {/* Alerts table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="text-sm" style={{ minWidth: 1100 }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--cedar-limestone)', borderBottom: '1px solid var(--cedar-limestone)' }}>
                <th className="text-left py-3 px-4 font-medium text-gray-500 w-8" />
                <th className="text-left py-3 px-4 font-medium text-gray-500" style={{ minWidth: 90 }}>Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500" style={{ minWidth: 220 }}>Exercise Name</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500" style={{ minWidth: 220 }}>Matched To</th>
                <th className="text-center py-3 px-4 font-medium text-gray-500" style={{ minWidth: 100 }}>Confidence</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500" style={{ minWidth: 120 }}>Match Type</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500" style={{ minWidth: 60 }}>Count</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500" style={{ minWidth: 100 }}>Last Seen</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500" style={{ minWidth: 120 }}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {alerts.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-gray-400">
                    {searchQuery
                      ? `No alerts matching "${searchQuery}"`
                      : 'No alerts for the selected filter.'}
                  </td>
                </tr>
              ) : (
                alerts.map(alert => {
                  const isExpanded = expandedRows.has(alert.id);
                  const isUpdating = updatingIds.has(alert.id);
                  const confColor = confidenceColor(alert.match_confidence);
                  return (
                    <React.Fragment key={alert.id}>
                      <tr
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${isExpanded ? 'bg-gray-50' : ''}`}
                        onClick={() => onToggleRow(alert.id)}
                      >
                        <td className="py-3 px-4">
                          {isExpanded
                            ? <ChevronDown className="h-4 w-4 text-gray-400" />
                            : <ChevronRight className="h-4 w-4 text-gray-400" />}
                        </td>
                        <td className="py-3 px-4"><StatusBadge status={alert.status} /></td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-900">{alert.exercise_name}</p>
                            {alert.normalized_slug && (
                              <p className="text-xs text-gray-400 mt-0.5 font-mono">{alert.normalized_slug}</p>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div>
                            <p className="font-medium text-gray-700">{alert.matched_display_name}</p>
                            <p className="text-xs text-gray-400 mt-0.5 font-mono">{alert.matched_slug}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold border ${confColor}`}>
                            {(alert.match_confidence * 100).toFixed(0)}%
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-xs text-gray-600">{tierLabel(alert.match_tier)}</span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="inline-flex items-center gap-1 text-gray-600">
                            <Hash className="h-3 w-3" />
                            {alert.occurrence_count}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right text-gray-500">{formatTimestamp(alert.last_seen)}</td>
                        <td className="py-3 px-4 text-right" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            {alert.status === 'new' && (
                              <>
                                <button
                                  onClick={() => onUpdateStatus(alert.id, 'reviewed')}
                                  disabled={isUpdating}
                                  className="p-1.5 rounded hover:bg-blue-50 text-blue-600 disabled:opacity-50"
                                  title="Mark reviewed"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => onUpdateStatus(alert.id, 'resolved')}
                                  disabled={isUpdating}
                                  className="p-1.5 rounded hover:bg-[var(--cedar-subtle)] text-[var(--cedar-primary)] disabled:opacity-50"
                                  title="Mark resolved"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => onUpdateStatus(alert.id, 'ignored')}
                                  disabled={isUpdating}
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-50"
                                  title="Ignore"
                                >
                                  <XCircle className="h-4 w-4" />
                                </button>
                              </>
                            )}
                            {alert.status === 'reviewed' && (
                              <>
                                <button
                                  onClick={() => onUpdateStatus(alert.id, 'resolved')}
                                  disabled={isUpdating}
                                  className="p-1.5 rounded hover:bg-[var(--cedar-subtle)] text-[var(--cedar-primary)] disabled:opacity-50"
                                  title="Mark resolved"
                                >
                                  <CheckCircle className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => onUpdateStatus(alert.id, 'ignored')}
                                  disabled={isUpdating}
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-400 disabled:opacity-50"
                                  title="Ignore"
                                >
                                  <XCircle className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={9} style={{ background: 'var(--surface-subtle)', padding: '20px 32px', borderBottom: '1px solid var(--border)' }}>
                            {/* Exercise info + Lottie preview */}
                            {alert.exercise_info && Object.keys(alert.exercise_info).length > 0 ? (
                              <div style={{ marginBottom: 20 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-strong)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
                                  Matched Exercise — {alert.matched_display_name}
                                </div>
                                <ExerciseInfoPanel
                                  info={alert.exercise_info}
                                  animationPath={alert.animation_path}
                                  displayName={alert.matched_display_name}
                                />
                              </div>
                            ) : alert.animation_path ? (
                              <div style={{ marginBottom: 20 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-strong)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
                                  Matched Exercise — {alert.matched_display_name}
                                </div>
                                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                                  <div>
                                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Visual (what user sees)</div>
                                    <LottieAdminPreview animationPath={alert.animation_path} displayName={alert.matched_display_name} />
                                  </div>
                                </div>
                              </div>
                            ) : null}

                            {/* Metadata strip */}
                            <div className="grid grid-cols-3 gap-4 text-sm pt-3" style={{ borderTop: '1px solid var(--border)' }}>
                              <div>
                                <span className="font-medium text-gray-500 text-xs uppercase tracking-wide">Animation Path</span>
                                <p className="text-gray-700 font-mono text-xs mt-1 break-all">{alert.animation_path || '--'}</p>
                              </div>
                              <div>
                                <span className="font-medium text-gray-500 text-xs uppercase tracking-wide">Workout Type</span>
                                <p className="text-gray-700 mt-1">{alert.workout_type || '--'}</p>
                              </div>
                              <div>
                                <span className="font-medium text-gray-500 text-xs uppercase tracking-wide">Created</span>
                                <p className="text-gray-700 mt-1">{alert.created_at ? new Date(alert.created_at).toLocaleString() : '--'}</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── File Uploads Tab ─────────────────────────────────────────────────────────

interface FileUploadsTabProps {
  alerts: FileUploadAlert[];
  total: number;
  statusCounts: Record<AlertStatus, number>;
  loading: boolean;
  error: string | null;
  statusFilter: AlertStatus | 'all';
  searchQuery: string;
  expandedRows: Set<string>;
  updatingIds: Set<string>;
  onStatusFilterChange: (filter: AlertStatus | 'all') => void;
  onSearchChange: (query: string) => void;
  onToggleRow: (id: string) => void;
  onUpdateStatus: (id: string, status: AlertStatus) => void;
  onRetry: () => void;
}

function FileUploadsTab({
  alerts,
  total,
  statusCounts,
  loading,
  error,
  statusFilter,
  searchQuery,
  expandedRows,
  updatingIds,
  onStatusFilterChange,
  onSearchChange,
  onToggleRow,
  onUpdateStatus,
  onRetry,
}: FileUploadsTabProps) {
  if (loading) return <LoadingState message="Loading file upload alerts..." />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;

  const totalAll = (statusCounts.new || 0) + (statusCounts.reviewed || 0) + (statusCounts.resolved || 0) + (statusCounts.ignored || 0);

  if (totalAll === 0 && statusFilter === 'all' && !searchQuery) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <Upload className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-700 mb-2">
            No file upload alerts yet
          </h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            Alerts will appear here when users try to upload files that can't be processed.
            This helps identify which file types to support next.
          </p>
        </CardContent>
      </Card>
    );
  }

  const REASON_LABELS: Record<string, string> = {
    unknown_content_type: 'Unknown Type',
    processing_error: 'Processing Error',
    processing_timeout: 'Timeout',
  };

  return (
    <div className="space-y-6">
      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-400" />
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            {(['all', 'new', 'reviewed', 'resolved', 'ignored'] as const).map(s => (
              <button
                key={s}
                onClick={() => onStatusFilterChange(s)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                  statusFilter === s
                    ? 'bg-[var(--cedar-primary)] text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                } ${s !== 'all' ? 'border-l border-gray-200' : ''}`}
              >
                {s === 'all' ? 'All' : STATUS_CONFIG[s].label}
                {s !== 'all' && (
                  <span className="ml-1 text-[10px] opacity-70">
                    ({statusCounts[s] || 0})
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--cedar-primary)]/20 focus:border-[var(--cedar-primary)]"
          />
        </div>

        <span className="text-xs text-gray-400 ml-auto">
          Showing {alerts.length} of {total} alerts
        </span>
      </div>

      {/* Alerts table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: 'var(--cedar-limestone)', borderBottom: '1px solid var(--cedar-limestone)' }}>
                <th className="text-left py-3 px-4 font-medium text-gray-500 w-8" />
                <th className="text-left py-3 px-4 font-medium text-gray-500">Status</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Filename</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Extension</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Content Type</th>
                <th className="text-left py-3 px-4 font-medium text-gray-500">Reason</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">Count</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">Last Seen</th>
                <th className="text-right py-3 px-4 font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {alerts.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-gray-400">
                    {searchQuery
                      ? `No alerts matching "${searchQuery}"`
                      : 'No alerts for the selected filter.'}
                  </td>
                </tr>
              ) : (
                alerts.map(alert => {
                  const isExpanded = expandedRows.has(alert.id);
                  const isUpdating = updatingIds.has(alert.id);
                  return (
                    <React.Fragment key={alert.id}>
                      <tr className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4">
                          <button onClick={() => onToggleRow(alert.id)} className="text-gray-400 hover:text-gray-600">
                            {isExpanded
                              ? <ChevronDown className="h-4 w-4" />
                              : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </td>
                        <td className="py-3 px-4">
                          <StatusBadge status={alert.status} />
                        </td>
                        <td className="py-3 px-4 font-medium text-gray-900 max-w-[200px] truncate">
                          {alert.filename}
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-block px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-700 font-mono">
                            {alert.file_extension || '--'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-600 max-w-[140px] truncate">
                          {alert.content_type}
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {REASON_LABELS[alert.alert_reason] || alert.alert_reason}
                        </td>
                        <td className="py-3 px-4 text-right font-mono text-gray-700">
                          {alert.occurrence_count}
                        </td>
                        <td className="py-3 px-4 text-right text-gray-500 text-xs whitespace-nowrap">
                          {formatTimestamp(alert.last_seen)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            {alert.status !== 'reviewed' && (
                              <ActionButton label="Review" icon={Eye} onClick={() => onUpdateStatus(alert.id, 'reviewed')} disabled={isUpdating} color="blue" />
                            )}
                            {alert.status !== 'resolved' && (
                              <ActionButton label="Resolve" icon={CheckCircle} onClick={() => onUpdateStatus(alert.id, 'resolved')} disabled={isUpdating} color="green" />
                            )}
                            {alert.status !== 'ignored' && (
                              <ActionButton label="Ignore" icon={XCircle} onClick={() => onUpdateStatus(alert.id, 'ignored')} disabled={isUpdating} color="gray" />
                            )}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-gray-50">
                          <td colSpan={9} className="px-4 py-4">
                            <div className="ml-8 space-y-3">
                              <h4 className="text-sm font-medium text-gray-700">Upload Details</h4>
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                                <div>
                                  <span className="text-gray-500 block mb-0.5">Detected File Type</span>
                                  <span className="font-medium text-gray-800">{alert.detected_file_type}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500 block mb-0.5">Processing Status</span>
                                  <span className="font-medium text-gray-800">{alert.processing_status}</span>
                                </div>
                                <div>
                                  <span className="text-gray-500 block mb-0.5">User</span>
                                  <span className="font-medium text-gray-800 font-mono">
                                    {alert.user_id ? alert.user_id.slice(0, 8) + '...' : '--'}
                                  </span>
                                </div>
                                {alert.error_details && (
                                  <div className="sm:col-span-3">
                                    <span className="text-gray-500 block mb-0.5">Error Details</span>
                                    <span className="font-medium text-gray-800">{alert.error_details}</span>
                                  </div>
                                )}
                              </div>
                              <div className="pt-2 border-t border-gray-200 flex gap-6 text-xs text-gray-500">
                                <span>Alert ID: <span className="font-mono">{alert.id}</span></span>
                                {alert.created_at && (
                                  <span>Created: {new Date(alert.created_at).toLocaleString()}</span>
                                )}
                                <span>Total for {alert.file_extension || 'this type'}: {alert.occurrence_count}</span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── Alert Row ────────────────────────────────────────────────────────────────

interface AlertRowProps {
  alert: ExerciseGapAlert;
  isExpanded: boolean;
  isUpdating: boolean;
  onToggle: () => void;
  onUpdateStatus: (id: string, status: AlertStatus) => void;
}

function AlertRow({ alert, isExpanded, isUpdating, onToggle, onUpdateStatus }: AlertRowProps) {
  return (
    <>
      <tr className="hover:bg-gray-50 transition-colors">
        {/* Expand toggle */}
        <td className="py-3 px-4">
          <button onClick={onToggle} className="text-gray-400 hover:text-gray-600">
            {isExpanded
              ? <ChevronDown className="h-4 w-4" />
              : <ChevronRight className="h-4 w-4" />}
          </button>
        </td>
        <td className="py-3 px-4">
          <StatusBadge status={alert.status} />
        </td>
        <td className="py-3 px-4 font-medium text-gray-900">
          <div>{alert.exercise_name}</div>
          {alert.normalized_slug && alert.normalized_slug !== alert.exercise_name.toLowerCase() && (
            <div className="text-xs text-gray-400 font-mono mt-0.5">{alert.normalized_slug}</div>
          )}
        </td>
        <td className="py-3 px-4 text-gray-600">
          <div>{alert.likely_cause?.replace(/_/g, ' ') || '--'}</div>
          {alert.suggested_category && (
            <div className="text-xs text-gray-400 mt-0.5">{alert.suggested_category}</div>
          )}
        </td>
        <td className="py-3 px-4 text-gray-600">
          {alert.equipment || '--'}
        </td>
        <td className="py-3 px-4">
          <div className="flex flex-wrap gap-1">
            {(alert.target_muscles || []).slice(0, 3).map(m => (
              <span
                key={m}
                className="inline-block px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600"
              >
                {m}
              </span>
            ))}
            {(alert.target_muscles || []).length > 3 && (
              <span className="text-xs text-gray-400">
                +{alert.target_muscles.length - 3}
              </span>
            )}
          </div>
        </td>
        <td className="py-3 px-4 text-right font-mono text-gray-700">
          {alert.occurrence_count}
        </td>
        <td className="py-3 px-4 text-right text-gray-500 text-xs whitespace-nowrap">
          {formatTimestamp(alert.last_seen)}
        </td>
        <td className="py-3 px-4 text-right">
          <div className="flex items-center justify-end gap-1">
            {alert.status !== 'reviewed' && (
              <ActionButton
                label="Review"
                icon={Eye}
                onClick={() => onUpdateStatus(alert.id, 'reviewed')}
                disabled={isUpdating}
                color="blue"
              />
            )}
            {alert.status !== 'resolved' && (
              <ActionButton
                label="Resolve"
                icon={CheckCircle}
                onClick={() => onUpdateStatus(alert.id, 'resolved')}
                disabled={isUpdating}
                color="green"
              />
            )}
            {alert.status !== 'ignored' && (
              <ActionButton
                label="Ignore"
                icon={XCircle}
                onClick={() => onUpdateStatus(alert.id, 'ignored')}
                disabled={isUpdating}
                color="gray"
              />
            )}
          </div>
        </td>
      </tr>

      {/* Expanded row */}
      {isExpanded && (
        <tr className="bg-gray-50">
          <td colSpan={9} className="px-4 py-4">
            <ExpandedDetail alert={alert} />
          </td>
        </tr>
      )}
    </>
  );
}

// ── Expanded detail ──────────────────────────────────────────────────────────

function ExpandedDetail({ alert }: { alert: ExerciseGapAlert }) {
  const llm = alert.llm_output;
  // Filter out "target_muscles" literal string that sometimes appears in form_cues
  const formCues = (llm?.form_cues || []).filter(
    c => typeof c === 'string' && c !== 'target_muscles' && c.trim().length > 0
  );

  return (
    <div className="ml-8 space-y-4">
      {/* Context row */}
      <div className="flex flex-wrap gap-4 text-xs">
        {alert.user_id && (
          <div className="px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-lg">
            <span className="text-blue-500">User</span>
            <span className="ml-1.5 font-mono text-blue-700">{alert.user_id}</span>
          </div>
        )}
        {alert.workout_type && (
          <div className="px-3 py-1.5 bg-purple-50 border border-purple-100 rounded-lg">
            <span className="text-purple-500">Workout Type</span>
            <span className="ml-1.5 font-medium text-purple-700">{alert.workout_type}</span>
          </div>
        )}
        {alert.suggested_category && (
          <div className="px-3 py-1.5 rounded-lg" style={{ backgroundColor: 'var(--cedar-subtle)', border: '1px solid var(--cedar-mist)' }}>
            <span style={{ color: 'var(--cedar-primary)' }}>Suggested Category</span>
            <span className="ml-1.5 font-medium" style={{ color: 'var(--cedar-dark)' }}>{alert.suggested_category}</span>
          </div>
        )}
        {alert.normalized_slug && (
          <div className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
            <span className="text-gray-500">Slug</span>
            <span className="ml-1.5 font-mono text-gray-700">{alert.normalized_slug}</span>
          </div>
        )}
      </div>

      {/* LLM output */}
      <h4 className="text-sm font-medium text-gray-700">LLM Output Details</h4>
      {!llm ? (
        <p className="text-xs text-gray-400 italic">No LLM output data available for this alert.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 text-xs">
          {llm.sets != null && (
            <div>
              <span className="text-gray-500 block mb-0.5">Sets</span>
              <span className="font-medium text-gray-800">{llm.sets}</span>
            </div>
          )}
          {llm.reps && (
            <div>
              <span className="text-gray-500 block mb-0.5">Reps</span>
              <span className="font-medium text-gray-800">{llm.reps}</span>
            </div>
          )}
          {llm.equipment && llm.equipment.length > 0 && (
            <div>
              <span className="text-gray-500 block mb-0.5">Equipment</span>
              <span className="font-medium text-gray-800">{llm.equipment.join(', ')}</span>
            </div>
          )}
          {llm.target_muscles && llm.target_muscles.length > 0 && (
            <div>
              <span className="text-gray-500 block mb-0.5">Target Muscles</span>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {llm.target_muscles.map((m, i) => (
                  <span key={i} className="inline-block px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{m}</span>
                ))}
              </div>
            </div>
          )}
          {llm.notes && (
            <div className="sm:col-span-4">
              <span className="text-gray-500 block mb-0.5">Notes</span>
              <span className="font-medium text-gray-800">{llm.notes}</span>
            </div>
          )}
          {formCues.length > 0 && (
            <div className="sm:col-span-4">
              <span className="text-gray-500 block mb-1">Form Cues</span>
              <ul className="list-disc list-inside space-y-0.5">
                {formCues.map((cue, i) => (
                  <li key={i} className="text-gray-800">{cue}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Metadata footer */}
      <div className="pt-2 border-t border-gray-200 flex flex-wrap gap-6 text-xs text-gray-500">
        <span>Alert ID: <span className="font-mono">{alert.id}</span></span>
        {alert.created_at && (
          <span>Created: {new Date(alert.created_at).toLocaleString()}</span>
        )}
        <span>Total occurrences: {alert.occurrence_count}</span>
      </div>
    </div>
  );
}

// ── Action Button ────────────────────────────────────────────────────────────

interface ActionButtonProps {
  label: string;
  icon: React.ElementType;
  onClick: () => void;
  disabled: boolean;
  color: 'blue' | 'green' | 'gray';
}

function ActionButton({ label, icon: Icon, onClick, disabled, color }: ActionButtonProps) {
  const colorMap = {
    blue: 'text-blue-600 hover:bg-blue-50',
    green: 'text-[var(--cedar-primary)] hover:bg-[var(--cedar-subtle)]',
    gray: 'text-gray-500 hover:bg-gray-100',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`p-1.5 rounded transition-colors ${colorMap[color]} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

// ── Summary Card ─────────────────────────────────────────────────────────────

interface SummaryCardProps {
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  label: string;
  value: string | number;
  isText?: boolean;
}

function SummaryCard({ icon: Icon, iconColor, iconBg, label, value, isText }: SummaryCardProps) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 py-5">
        <div className={`p-3 rounded-lg ${iconBg}`}>
          <Icon className={`h-5 w-5 ${iconColor}`} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-500">{label}</p>
          <p className={`font-bold truncate ${isText ? 'text-sm text-gray-800' : 'text-2xl text-gray-900'}`}>
            {value}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Shared states ────────────────────────────────────────────────────────────

function LoadingState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-20">
      <RefreshCw className="h-8 w-8 text-[var(--cedar-light)] animate-spin mb-4" />
      <p className="text-sm text-gray-500">{message}</p>
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <AlertTriangle className="h-10 w-10 text-red-400 mx-auto mb-3" />
        <p className="text-sm text-gray-700 mb-1">Failed to load data</p>
        <p className="text-xs text-gray-500 mb-4 max-w-md mx-auto">{message}</p>
        <button
          onClick={onRetry}
          className="px-4 py-2 text-sm font-medium text-white bg-[var(--cedar-primary)] rounded-lg hover:bg-[var(--cedar-primary)] transition-colors"
        >
          Retry
        </button>
      </CardContent>
    </Card>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardContent className="py-16 text-center">
        <Image className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-700 mb-2">
          No exercise gap alerts yet
        </h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          Alerts will appear here when the AI generates exercises that don't have matching
          animations. This helps identify gaps in the Lottie exercise library.
        </p>
      </CardContent>
    </Card>
  );
}

// ── Plan Quality Tab ────────────────────────────────────────────────────────

interface PlanQualityIssue {
  problem_category: string;
  why_it_matters: string;
  suggested_action: string;
  session_title: string;
  session_type: string;
  exercise_name: string | null;
  detail: string;
}

interface PlanQualityAlert {
  id: string;
  title: string;
  message: string;
  created_at: string | null;
  status: string;
  model_used: string | null;
  week_start: string | null;
  total_sessions: number;
  affected_sessions: number;
  issues: PlanQualityIssue[];
  category_counts: Record<string, number>;
}

interface PlanQualitySummary {
  total_alerts: number;
  total_issues: number;
  plans_affected: number;
  period_days: number;
}

interface PlanQualityResponse {
  success: boolean;
  summary: PlanQualitySummary;
  category_breakdown: Record<string, number>;
  alerts: PlanQualityAlert[];
}

interface PlanQualityTabProps {
  data: PlanQualityResponse | null;
  loading: boolean;
  error: string | null;
  expandedCategories: Set<string>;
  onToggleCategory: (cat: string) => void;
  onRetry: () => void;
}

const CATEGORY_LABELS: Record<string, { label: string; color: string }> = {
  coaching_hollowness: { label: 'Coaching Hollowness', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  academic_leak: { label: 'Academic Leak', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  generic_form_cue: { label: 'Generic Form Cue', color: 'bg-red-100 text-red-700 border-red-200' },
  generic_safety_tip: { label: 'Generic Safety Tip', color: 'bg-red-100 text-red-700 border-red-200' },
  missing_safety_notes: { label: 'Missing Safety Notes', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  insufficient_form_detail: { label: 'Insufficient Form Detail', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  missing_phase_guidance: { label: 'Missing Phase Guidance', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  missing_weather_context: { label: 'Missing Weather Context', color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
};

function PlanQualityTab({ data, loading, error, expandedCategories, onToggleCategory, onRetry }: PlanQualityTabProps) {
  // THIS HOOK MUST STAY ABOVE THE EARLY RETURNS BELOW.
  //
  // It used to sit after them, which crashed the entire admin portal. The three
  // guards depend on `loading` / `error` / `data`, and all three change WHILE
  // this component is mounted:
  //
  //   The page-header "Refresh" button is rendered outside the
  //   `{activeTab === 'plan-quality' && …}` conditional, so it is present on
  //   every tab. It calls fetchPlanQuality(), which does setPlanQualityLoading(true)
  //   and clears it in a finally. `activeTab` does not change, so there is NO
  //   remount — the same fiber re-renders and returns at `if (loading)` before
  //   reaching this hook. React sees 0 hooks against the 1 it recorded and throws
  //   "Rendered fewer hooks than expected"; when the fetch settles it goes 0 -> 1
  //   and throws again. The mount-effect fetch and the error state's onRetry
  //   button reach the same transition.
  //
  // There is no ErrorBoundary anywhere in AdminApp.tsx, so that throw unmounts
  // the whole admin React root — the entire portal blanks, not just this tab.
  //
  // Same shape as DebriefButton.native.tsx (40 events / 4 users / 30 days), fixed
  // in 4dca1f38f by the identical hoist. `data` is legitimately null on the
  // loading and error renders, hence the `?.` — without it this position throws.
  const issuesByCategory = useMemo(() => {
    const map: Record<string, PlanQualityIssue[]> = {};
    for (const alert of data?.alerts || []) {
      for (const issue of alert.issues || []) {
        const cat = issue.problem_category;
        (map[cat] ??= []).push(issue);
      }
    }
    return map;
  }, [data?.alerts]);

  if (loading) return <LoadingState message="Loading plan quality alerts..." />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;
  if (!data || !data.alerts || data.alerts.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <CheckCircle className="h-12 w-12 mx-auto mb-4" style={{ color: 'var(--cedar-light)' }} />
          <h3 className="text-lg font-medium text-gray-700 mb-2">No plan quality warnings</h3>
          <p className="text-sm text-gray-500 max-w-md mx-auto">
            All weekly plans passed quality checks. Warnings appear here when LLM-generated
            plans have suboptimal coaching content (generic cues, missing safety notes, etc.).
          </p>
        </CardContent>
      </Card>
    );
  }

  const summary = data.summary;
  const categoryBreakdown = data.category_breakdown || {};


  return (
    <div className="space-y-6">
      {/* Summary bar */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="font-medium text-gray-900">{summary.total_issues || 0}</span>
              <span className="text-gray-500 ml-1">quality issues</span>
            </div>
            <div className="h-4 w-px bg-gray-200" />
            <div>
              <span className="font-medium text-gray-900">{summary.plans_affected || 0}</span>
              <span className="text-gray-500 ml-1">plans affected</span>
            </div>
            <div className="h-4 w-px bg-gray-200" />
            <div>
              <span className="text-gray-500">Last {summary.period_days || 7} days</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category breakdown */}
      <div className="space-y-3">
        {Object.entries(categoryBreakdown)
          .sort(([, a], [, b]) => b - a)
          .map(([category, count]) => {
            const catConfig = CATEGORY_LABELS[category] || { label: category, color: 'bg-gray-100 text-gray-700 border-gray-200' };
            const isExpanded = expandedCategories.has(category);
            const categoryIssues = issuesByCategory[category] || [];

            return (
              <Card key={category}>
                <button
                  onClick={() => onToggleCategory(category)}
                  className="w-full text-left"
                >
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${catConfig.color}`}>
                          {catConfig.label}
                        </span>
                        <span className="text-sm font-medium text-gray-900">{count} issue{count !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    {/* Why it matters - always visible */}
                    {categoryIssues[0]?.why_it_matters && (
                      <p className="text-xs text-gray-500 mt-1 ml-7">{categoryIssues[0].why_it_matters}</p>
                    )}
                  </CardContent>
                </button>

                {/* Expanded: individual issues */}
                {isExpanded && categoryIssues.length > 0 && (
                  <div className="border-t border-gray-100">
                    <div className="divide-y divide-gray-50">
                      {categoryIssues.slice(0, 20).map((issue: PlanQualityIssue, idx: number) => (
                        <div key={idx} className="px-6 py-2.5 text-sm">
                          <div className="flex items-start gap-2">
                            <span className="text-gray-400 text-xs mt-0.5 w-4">{idx + 1}.</span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-gray-700">{issue.session_title}</span>
                                {issue.session_type && (
                                  <span className="text-xs text-gray-400">{issue.session_type}</span>
                                )}
                              </div>
                              {issue.exercise_name && (
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Exercise: <span className="font-medium">{issue.exercise_name}</span>
                                </p>
                              )}
                              {issue.detail && (
                                <p className="text-xs text-gray-400 mt-0.5 truncate">{issue.detail}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {/* Suggested action */}
                    {categoryIssues[0]?.suggested_action && (
                      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
                        <p className="text-xs text-gray-600">
                          <span className="font-medium">Suggested action:</span> {categoryIssues[0].suggested_action}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
      </div>
    </div>
  );
}

// ── Corpus Health Tab (Phase 1.3) ────────────────────────────────────────────
// Renders the four diagnostic analyses (A/B/C/D) from
// /api/admin/lottie-diagnostics. Read-only view; the "Refresh" button forces
// a cache bypass so admins can re-run the SQL scans on demand.

interface CorpusHealthTabProps {
  data: CorpusDiagnostics | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

function _triageLabel(triage: string): string {
  switch (triage) {
    case 'apostrophe_normalization': return 'Apostrophe (Phase 2a)';
    case 'slug_alias_needed':        return 'Slug alias (Phase 2b)';
    case 'genuine_corpus_gap':       return 'Genuine gap (needs new animation)';
    case 'false_positive_match':     return 'False positive (Phase 2c)';
    default:                          return triage;
  }
}

function _triageClass(triage: string): string {
  switch (triage) {
    case 'apostrophe_normalization': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'slug_alias_needed':        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    case 'genuine_corpus_gap':       return 'bg-gray-50 text-gray-700 border-gray-200';
    case 'false_positive_match':     return 'bg-red-50 text-red-700 border-red-200';
    default:                          return 'bg-gray-50 text-gray-700 border-gray-200';
  }
}

function CorpusHealthTab({ data, loading, error, onRefresh }: CorpusHealthTabProps) {
  if (loading) return <LoadingState message="Running corpus diagnostic…" />;
  if (error) return <ErrorState message={error} onRetry={onRefresh} />;
  if (!data) return <EmptyState />;

  const { files_vs_registry: fvr, slug_quality: sq, production_gaps: pg, coverage: cov } = data;

  return (
    <div className="space-y-6">
      {/* Header strip */}
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          Generated: {new Date(data.generated_at_utc).toLocaleString()}
          {data.cached && (
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
              cached
            </span>
          )}
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="h-3 w-3" />
          Recompute
        </button>
      </div>

      {/* ── Overview cards ── */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-gray-500 mb-1">Overall coverage ({cov.days_window}d)</div>
            <div className="text-2xl font-bold text-[var(--text-primary)]">{cov.overall_coverage_pct}%</div>
            <div className="text-xs text-gray-500 mt-1">
              {cov.overall_with_animation} / {cov.overall_total} exercises
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-gray-500 mb-1">Files on disk</div>
            <div className="text-2xl font-bold text-[var(--text-primary)]">{fvr.files_on_disk_count}</div>
            <div className="text-xs text-gray-500 mt-1">
              {fvr.registry_entries_count} registry entries
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-gray-500 mb-1">Orphans / Dangling</div>
            <div className="text-2xl font-bold text-[var(--text-primary)]">
              {fvr.orphan_count} / {fvr.dangling_count}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              disk &not;in registry / registry &not;on disk
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs text-gray-500 mb-1">Slug violations</div>
            <div className="text-2xl font-bold text-[var(--text-primary)]">{sq.violation_count}</div>
            <div className="text-xs text-gray-500 mt-1">
              {sq.total_entries_scanned} entries scanned
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ── Triage summary ── */}
      {pg.triage_summary && Object.keys(pg.triage_summary).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">Production gap triage (last {pg.days_window}d)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(pg.triage_summary)
                .sort(([, a], [, b]) => b - a)
                .map(([cat, count]) => (
                  <div key={cat} className={`border rounded-lg p-3 ${_triageClass(cat)}`}>
                    <div className="text-xs font-medium opacity-80">{_triageLabel(cat)}</div>
                    <div className="text-xl font-bold mt-1">{count}</div>
                    <div className="text-[10px] mt-1 opacity-70">total occurrences</div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── Coverage by content type ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-semibold">Coverage by content type (last {cov.days_window}d)</CardTitle>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left py-2">Content type</th>
                <th className="text-right py-2">Total</th>
                <th className="text-right py-2">With animation</th>
                <th className="text-right py-2">Placeholders</th>
                <th className="text-right py-2">Coverage %</th>
              </tr>
            </thead>
            <tbody>
              {cov.by_content_type.length === 0 && (
                <tr><td colSpan={5} className="text-center py-4 text-gray-400">No production data in window.</td></tr>
              )}
              {cov.by_content_type.map(row => (
                <tr key={row.content_type} className="border-t border-gray-100">
                  <td className="py-2 font-mono text-xs">{row.content_type}</td>
                  <td className="py-2 text-right">{row.total.toLocaleString()}</td>
                  <td className="py-2 text-right">{row.with_animation.toLocaleString()}</td>
                  <td className="py-2 text-right">{row.placeholders.toLocaleString()}</td>
                  <td className={`py-2 text-right font-medium ${
                    row.coverage_pct >= 75 ? 'text-green-700' :
                    row.coverage_pct >= 50 ? 'text-yellow-700' : 'text-red-700'
                  }`}>{row.coverage_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* ── Slug-quality violations ── */}
      {sq.violations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">
              Slug-quality violations ({sq.violation_count})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500 mb-3">
              Single-token slugs with multi-token Lottie names and no aliases. These are
              Phase 2b candidates — adding the suggested alias to <code>lottie_exercise_lookup.json</code> moves
              the match from tier-4 fuzzy (≈0.55 confidence) to tier-2 exact (1.0).
            </p>
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left py-2">Slug</th>
                  <th className="text-left py-2">Display name</th>
                  <th className="text-left py-2">Internal name</th>
                  <th className="text-left py-2">Suggested aliases</th>
                </tr>
              </thead>
              <tbody>
                {sq.violations.map(v => (
                  <tr key={v.slug} className="border-t border-gray-100">
                    <td className="py-2 font-mono text-xs">{v.slug}</td>
                    <td className="py-2">{v.display_name}</td>
                    <td className="py-2 text-gray-600">{v.internal_name}</td>
                    <td className="py-2 font-mono text-xs">
                      {v.suggested_aliases.map(a => (
                        <span key={a} className="inline-block bg-yellow-50 text-yellow-700 border border-yellow-200 rounded px-1.5 py-0.5 mr-1 mb-1">
                          {a}
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ── Top gap alerts ── */}
      {pg.top_gaps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">
              Top exercise gaps (last {pg.days_window}d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500 mb-3">
              Exercise names the LLM produced that found no Lottie match. Triage tells
              you which fix lever applies.
            </p>
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left py-2">Exercise</th>
                  <th className="text-right py-2">Occurrences</th>
                  <th className="text-left py-2 pl-4">Triage</th>
                </tr>
              </thead>
              <tbody>
                {pg.top_gaps.slice(0, 25).map((g, i) => (
                  <tr key={`${g.normalized_slug}-${i}`} className="border-t border-gray-100">
                    <td className="py-2">{g.exercise_name}</td>
                    <td className="py-2 text-right">{g.occurrences.toLocaleString()}</td>
                    <td className="py-2 pl-4">
                      <span className={`inline-block text-xs px-2 py-0.5 rounded border ${_triageClass(g.triage)}`}>
                        {_triageLabel(g.triage)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ── Top near-misses ── */}
      {pg.top_near_misses.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">
              Top near-miss matches (last {pg.days_window}d)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-gray-500 mb-3">
              Fuzzy matches (tier 3-4) below the 0.7 review threshold. Tier-3 below 0.6
              is flagged as a likely false positive — Phase 2c candidates.
            </p>
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left py-2">Exercise</th>
                  <th className="text-left py-2">Matched slug</th>
                  <th className="text-right py-2">Conf</th>
                  <th className="text-right py-2">Tier</th>
                  <th className="text-right py-2">Occurrences</th>
                  <th className="text-center py-2">Likely FP?</th>
                </tr>
              </thead>
              <tbody>
                {pg.top_near_misses.slice(0, 25).map((nm, i) => (
                  <tr key={`${nm.normalized_slug}-${i}`} className="border-t border-gray-100">
                    <td className="py-2">{nm.exercise_name}</td>
                    <td className="py-2 font-mono text-xs">{nm.matched_slug}</td>
                    <td className="py-2 text-right">{nm.match_confidence.toFixed(3)}</td>
                    <td className="py-2 text-right">{nm.match_tier}</td>
                    <td className="py-2 text-right">{nm.occurrences.toLocaleString()}</td>
                    <td className="py-2 text-center">
                      {nm.likely_false_positive ? (
                        <span className="inline-block text-xs px-2 py-0.5 rounded border bg-red-50 text-red-700 border-red-200">
                          YES
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">no</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* ── File integrity (orphans / dangling) ── */}
      {(fvr.orphan_count > 0 || fvr.dangling_count > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">File integrity</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {fvr.orphan_count > 0 && (
              <details>
                <summary className="cursor-pointer text-sm font-medium">
                  {fvr.orphan_count} orphan file(s) — on disk, no registry entry
                </summary>
                <ul className="mt-2 text-xs font-mono space-y-1 max-h-64 overflow-y-auto">
                  {fvr.orphan_files.slice(0, 100).map(f => (
                    <li key={f} className="text-gray-600">{f}</li>
                  ))}
                </ul>
              </details>
            )}
            {fvr.dangling_count > 0 && (
              <details>
                <summary className="cursor-pointer text-sm font-medium">
                  {fvr.dangling_count} dangling ref(s) — registry entry, no file on disk
                </summary>
                <ul className="mt-2 text-xs font-mono space-y-1 max-h-64 overflow-y-auto">
                  {fvr.dangling_refs.slice(0, 100).map(f => (
                    <li key={f} className="text-red-600">{f}</li>
                  ))}
                </ul>
              </details>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
